import PropTypes from 'prop-types';
import React, {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useRoomsContext } from './roomsContext';
import { usePrevious } from '../hooks/usePrevious';

const RefsContext = createContext(null);

export const RefsContextProvider = ({ children }) => {
  const { rooms } = useRoomsContext();
  const prevRooms = usePrevious(rooms);
  const removeButtons = useRef([]);
  const addRoomButton = useRef();

  const updateRemoveButtons = useCallback((roomIndex, ref) => {
    // Add or update existing ref
    if (roomIndex && ref?.current && ref?.current !== removeButtons.current[roomIndex]) {
      removeButtons.current[roomIndex] = ref?.current;
    }

    // Delete existing ref
    if (roomIndex && ref === null && removeButtons.current[roomIndex]) {
      delete removeButtons.current[roomIndex];
    }
  }, []);

  useEffect(() => {
    if (prevRooms && prevRooms.length > 0) {
      const lastRoomIndex = rooms.length - 1;
      const lastRemoveButton = removeButtons?.current[lastRoomIndex];
      const isNewRoomAdded = prevRooms.length < rooms.length;
      const isOldRoomRemoved = prevRooms.length > rooms.length;

      // Focus on remove button when a new room is added - performance improvement
      if (lastRemoveButton && isNewRoomAdded) {
        lastRemoveButton.focus();
      }

      // Focus on add button only when a room is removed - performance improvement
      if (isOldRoomRemoved) {
        addRoomButton?.current?.focus();
      }
    }
  }, [addRoomButton, prevRooms, removeButtons, rooms]);

  const memoized = useMemo(() => ({
    addRoomButton,
    removeButtons,
    updateRemoveButtons,
  }), [updateRemoveButtons]);

  return (
    <RefsContext.Provider value={memoized}>
      {children}
    </RefsContext.Provider>
  );
};

RefsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useRefsContext = () => {
  const context = useContext(RefsContext);

  if (!context || Object.keys(context).length === 0) {
    throw new Error('useRefsContext must be used within RefsContextProvider.');
  }

  return context;
};
